import { useState } from 'react';

import { UpgradeModal } from './UpgradeModal';
import Icon from '@components/atoms/icon';
import Tooltip from '@components/atoms/tooltip';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';

const MEMBERS_ONLY_TITLE = 'Available to FFHUB Members';

type MembersOnlyLockProps = {
  /**
   * Feature name used in the upgrade modal copy, as it reads mid-sentence
   * (e.g. "predicted points").
   */
  feature: string;
  /**
   * Where this padlock sits, for the feature_locked_hit analytics event
   * (FFH-2373), e.g. "pick_chip_banner".
   */
  placement: string;
  /** Optional copy shown next to the padlock (e.g. a stat label). */
  label?: string;
  title?: string;
  dataCy?: string;
};

/**
 * Inline padlock that replaces premium values (predicted points, etc.) for
 * non-paying members. Clicking it opens the upgrade modal explaining the
 * feature is members-only, with a link through to the join page.
 */
export const MembersOnlyLock = ({
  feature,
  placement,
  label,
  title = MEMBERS_ONLY_TITLE,
  dataCy
}: MembersOnlyLockProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <Tooltip variant="dark" title={title}>
        <button
          type="button"
          onClick={() => {
            // FFH-2373: hitting the lock is the demand signal for the gated
            // feature; the modal's own CTA fires upgrade_cta_clicked, so the
            // pair reads as lock hit -> upgrade intent.
            captureAnalyticsEvent(AnalyticsEvents.featureLockedHit, {
              placement,
              feature
            });
            setIsModalOpen(true);
          }}
          className="inline-flex items-center gap-1 text-primary-600"
          data-cy={dataCy ?? 'members-only-lock'}
          aria-label={title}
        >
          <Icon name="Lock" fontSize="16px" />
          {label && <span className="text-sm font-bold">{label}</span>}
        </button>
      </Tooltip>
      <UpgradeModal
        isOpen={isModalOpen}
        feature={feature}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default MembersOnlyLock;
